"use client"

import { useState, useEffect } from "react"
import { Eye, EyeOff, AlertCircle, X, ArrowLeft } from "lucide-react"
import usersService from "../../services/usersService"
import { toast } from "react-hot-toast"
import SelectCountry from "../SelectCountry"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"

 function CompleteProfileModal({ currentUser, onProfileComplete }) {
  const [isOpen, setIsOpen] = useState(false)
  const [passwordToggle, setPasswordToggle] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [userType, setUserType] = useState("")
  const [currentStep, setCurrentStep] =  useState("user-type") // user-type, profile-form

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    password: "",
    confirmPassword: "",
    website: "",
    country: "",
    phone: "",
    agreeToTerms: false,
  })

  // Check if profile is incomplete
  const isProfileIncomplete = (user) => {
    if (!user) return false
    return !user.fullName || !user.phone || !user.country || !user.businessName || !user.userType
  }

  // Check if modal should be shown when currentUser changes
  useEffect(() => {
    if (currentUser && isProfileIncomplete(currentUser) && currentUser.role?.name === 'Client') {
      setIsOpen(true)

      // Pre-fill form data
      const [firstName = "", lastName = ""] = (currentUser.fullName || "").split(" ")
      setProfileData({
        firstName,
        lastName,
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        country: currentUser.country || "",
        businessName: currentUser.businessName || "",
        website: currentUser.website || "",
        password: "",
        confirmPassword: "",
        agreeToTerms: false,
      })

      // If user already has userType, skip to profile form
      if (currentUser.userType) {
        setUserType(currentUser.userType)
        setCurrentStep("profile-form")
      } else {
        setCurrentStep("user-type")
      }
    } else {
      setIsOpen(false)
    }
  }, [currentUser])

  const handleCompleteProfile = async (e) => {
    e.preventDefault()

    // Validation
    if (
      !profileData.firstName ||
      !profileData.lastName ||
      !profileData.country ||
      !profileData.phone ||
      !profileData.businessName
    ) {
      toast.error("All required fields must be filled.", {
        duration: 3000,
        position: "top-center",
      })
      return
    }

    if (profileData.password && profileData.password !== profileData.confirmPassword) {
      toast.error("Passwords do not match.", {
        duration: 3000,
        position: "top-center",
      })
      return
    }

    if (!profileData.agreeToTerms) {
      toast.error("You must agree to the terms and conditions.", {
        duration: 3000,
        position: "top-center",
      })
      return
    }

    try {
      setErrorMessage("")
      setLoading(true)

      const updateData = {
        fullName: `${profileData.firstName} ${profileData.lastName}`,
        businessName: profileData.businessName,
        website: profileData.website,
        country: profileData.country,
        phone: profileData.phone,
        userType: userType,
      }

      // Only include password if provided
      if (profileData.password) {
        updateData.password = profileData.password
      }

      const response = await usersService.updateProfile(updateData)

      if (response.data && response.data.statusCode === 200) {
        toast.success("Profile completed successfully!", {
          duration: 3000,
          position: "top-center",
        })
        setIsOpen(false)
        if (onProfileComplete) {
          onProfileComplete()
        }
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Failed to update profile. Please try again.")
      console.error("Profile update failed:", error.response?.data)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    // Don't allow closing if profile is incomplete
    if (isProfileIncomplete(currentUser)) {
      toast.error("Please complete your profile to continue.", {
        duration: 3000,
        position: "top-center",
      })
      return
    }
    setIsOpen(false)
  }

  const renderUserTypeSelection = () => (
    <div className="space-y-4">
      <div className="text-start mb-6">
        <h2 className="text-2xl font-font-[500] text-black">Complete Your Profile</h2>
        <p className="text-neutral-600 mt-2">How do you plan to work with us?</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => {
            setUserType("advertiser")
            setCurrentStep("profile-form")
          }}
          className="block w-full flex justify-center items-center bg-white border border-neutral-200 rounded-lg h-14 text-neutral-700 text-sm hover:border-black hover:bg-neutral-50 transition-all"
        >
          <span className="font-medium text-black">I am a direct Advertiser</span>
        </button>

        <button
          onClick={() => {
            setUserType("agency")
            setCurrentStep("profile-form")
          }}
          className="block w-full flex justify-center items-center bg-white border border-neutral-200 rounded-lg h-14 text-neutral-700 text-sm hover:border-black hover:bg-neutral-50 transition-all"
        >
          <span className="font-medium text-black">I am an Agency</span>
        </button>

        <button
          onClick={() => {
            setUserType("partner")
            setCurrentStep("profile-form")
          }}
          className="block w-full flex justify-center items-center bg-white border border-neutral-200 rounded-lg h-14 text-neutral-700 text-sm hover:border-black hover:bg-neutral-50 transition-all"
        >
          <span className="font-medium text-black">I want to become a Partner</span>
        </button>
      </div>
    </div>
  )

  const renderProfileForm = () => {
    const userTypeText = {
      advertiser: "Advertiser",
      agency: "Agency",
      partner: "Partner",
    }

    return (
      <div className="space-y-4">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setCurrentStep("user-type")}
            className="flex items-center text-neutral-600 hover:text-black mb-4 text-sm"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back
          </button>
          <h2 className="text-2xl font-[500] text-black">Complete Your Profile</h2>
          <p className="text-neutral-600 mt-2">
            Account type: <span className="font-medium">{userTypeText[userType]}</span>
          </p>
        </div>

        <form onSubmit={handleCompleteProfile} className="space-y-4">
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 text-sm bg-red-50 border border-red-200 rounded-md text-red-600">
              <AlertCircle size={16} />
              <p>{errorMessage}</p>
            </div>
          )}

          {/* Email (readonly) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Email</label>
            <input
              type="email"
              value={profileData.email}
              readOnly
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-neutral-500 bg-neutral-50 text-sm"
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Name and Surname*</label>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="First Name"
                value={profileData.firstName}
                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black"
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black"
                required
              />
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Phone Number*</label>
            <PhoneInput
              country={"ma"}
              value={profileData.phone}
              onChange={(value) => setProfileData({ ...profileData, phone: value })}
              inputClass="!w-full !px-3 !pl-12 !py-2 !border !border-neutral-300 !rounded-lg !h-11 !text-black !placeholder-neutral-400 !text-sm !focus:outline-none !focus:border-black"
              containerClass="!w-full"
              buttonClass="!bg-gray-100 !border-neutral-300"
              dropdownClass="!bg-white !text-black"
            />
          </div>

          {/* Country */}
          <div className="space-y-2">
            <SelectCountry
              id="country"
              label="Country*"
              value={profileData.country}
              onChange={(value) => setProfileData({ ...profileData, country: value })}
            />
          </div>

          {/* Business Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Business Name*</label>
            <input
              type="text"
              placeholder="Corp Media"
              value={profileData.businessName}
              onChange={(e) => setProfileData({ ...profileData, businessName: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black"
              required
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Website</label>
            <input
              type="url"
              placeholder="corpmedia.com"
              value={profileData.website}
              onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black"
            />
          </div>

          {/* Password (optional) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700 text-start">Set Password (Optional)</label>
            <div className="relative">
              <input
                type={passwordToggle ? "text" : "password"}
                placeholder="Create a password"
                value={profileData.password}
                onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black pr-10"
              />
              <button
                type="button"
                onClick={() => setPasswordToggle(!passwordToggle)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {passwordToggle ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Leave blank to keep using Google login only.</p>
          </div>

          {/* Confirm Password */}
          {profileData.password && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700 text-start">Confirm Password*</label>
              <div className="relative">
                <input
                  type={passwordToggle ? "text" : "password"}
                  placeholder="Confirm password"
                  value={profileData.confirmPassword}
                  onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg h-11 text-black placeholder-neutral-400 text-sm focus:outline-none focus:border-black pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setPasswordToggle(!passwordToggle)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {passwordToggle ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          {/* Terms */}
          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              checked={profileData.agreeToTerms}
              onChange={(e) => setProfileData({ ...profileData, agreeToTerms: e.target.checked })}
              className="h-4 w-4 text-black border-neutral-300 rounded focus:ring-black"
              required
            />
            <label className="text-xs text-neutral-600">
              I agree to the{" "}
              <a href="#" className="text-black hover:underline">
                Terms & Conditions
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-4 bg-neutral-900 text-sm hover:bg-neutral-800 disabled:bg-neutral-400 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors duration-200"
          >
            {loading ? (
              <div className="flex justify-center items-center">
                <div className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              </div>
            ) : (
              "Complete Profile"
            )}
          </button>
        </form>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white  md:p-5 p-3 text-start rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
     

        <div className="p-6">{currentStep === "user-type" ? renderUserTypeSelection() : renderProfileForm()}</div>
      </div>
    </div>
  )
}

export default CompleteProfileModal